<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import { Icon } from '@iconify/vue'
import AccountPicker from '@/onenetwork/components/account_picker/AccountPicker.vue'
import RegionSelectComponent from '@/modules/broker/components/RegionSelectComponent.vue'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import AccountAPI from '@/modules/account/constants/account.api.uri.const'
import { regions as regionsConst } from '@/onenetwork/constants/onenetwork.regions.const'

interface Props {
  show: boolean
  initialClauses?: ConditionClause[] | null
}

interface Region {
  id: number | string
  name: string
  level1: { id?: number | string; name: string }
}

interface ConditionClause {
  key: string
  op: string
  value: unknown
}

interface AccountLike {
  id?: number | string
  name?: string
  phone?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'on:close': []
  'on:confirm': [payload: { label: string; clauses: ConditionClause[] }]
}>()

// ---- Form state ----
const form = reactive({
  // AccountPicker emits an array of selected accounts (even for single select)
  exact_account: [] as AccountLike[],
  keyword: '',
  gender: '',
  age: '',
  register_date_from: '',
  register_date_to: '',
  birthday_day: '',
  birthday_month: '',
  kyc_status: '',
  account_types: [] as string[],
  broker_area_regions: [] as Region[],
  device_type: '',
  last_active: '',
})

// ---- Select options ----
const genderOptions = [
  { label: 'Nam', value: 'male' },
  { label: 'Nữ', value: 'female' },
  { label: 'Khác', value: 'other' },
]

const ageOptions = [
  { label: 'Tuổi > 18 AND tuổi <= 30', value: 'age_18_30' },
  { label: 'Tuổi > 30 AND tuổi <= 45', value: 'age_30_45' },
  { label: 'Tuổi > 45', value: 'age_45_plus' },
]

const kycOptions = [
  { label: 'Chưa KYC', value: 'not_kyc' },
  { label: 'Đang xử lý', value: 'pending' },
  { label: 'Đã KYC', value: 'verified' },
]

const deviceOptions = [
  { label: 'người dùng iOS', value: 'ios' },
  { label: 'người dùng Android', value: 'android' },
  { label: 'Tất cả thiết bị', value: 'all' },
]

const lastActiveOptions = [
  { label: '> 30 ngày', value: 'gt_30' },
  { label: '> 7 ngày', value: 'gt_7' },
  { label: 'Trong 7 ngày', value: 'lt_7' },
]

const dayOptions = Array.from({ length: 31 }, (_, i) => ({
  label: String(i + 1).padStart(2, '0'),
  value: String(i + 1).padStart(2, '0'),
}))

const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  label: String(i + 1).padStart(2, '0'),
  value: String(i + 1).padStart(2, '0'),
}))

// Days per month (non-leap year)
const daysPerMonth: Record<string, number> = {
  '01': 31,
  '02': 29, // simplified - backend will handle Feb 29 validation
  '03': 31,
  '04': 30,
  '05': 31,
  '06': 30,
  '07': 31,
  '08': 31,
  '09': 30,
  '10': 31,
  '11': 30,
  '12': 31,
}

const filteredDayOptions = computed(() => {
  if (!form.birthday_month) return dayOptions
  const maxDays = daysPerMonth[form.birthday_month] || 31
  return dayOptions.filter((d) => parseInt(d.value) <= maxDays)
})

// Validate birthday: if day exceeds max days for selected month, reset day
watch(
  () => form.birthday_month,
  (month) => {
    if (month && form.birthday_day) {
      const maxDays = daysPerMonth[month] || 31
      if (parseInt(form.birthday_day) > maxDays) {
        form.birthday_day = ''
      }
    }
  },
)

const accountTypeOptions = [
  { label: 'Tất cả loại tài khoản', value: 'all' },
  { label: 'Tất cả môi giới', value: 'broker' },
  { label: 'Khách', value: 'customer' },
  { label: 'Cấp trên trực tiếp', value: 'direct_superior' },
  { label: 'Giám đốc hệ thống', value: 'system_director' },
]

// ---- Validation errors ----
const errors = reactive<Record<string, string>>({})

// Keyword special characters regex - allows letters (incl. Vietnamese), numbers, spaces, and basic punctuation
const SPECIAL_CHAR_REGEX = /[^a-zA-Z0-9\s\-_.()À-ÿ]/gi

const validateBirthday = (): string | null => {
  if (!form.birthday_day && !form.birthday_month) return null
  if (!form.birthday_day || !form.birthday_month) {
    return 'Phải chọn đầy đủ ngày và tháng sinh'
  }
  const maxDays = daysPerMonth[form.birthday_month] || 31
  if (parseInt(form.birthday_day) > maxDays) {
    return `Tháng ${form.birthday_month} chỉ có ${maxDays} ngày`
  }
  return null
}

// Handle "Tất cả loại tài khoản" checkbox
watch(
  () => form.account_types,
  (types, prevTypes) => {
    const wasAllChecked = prevTypes?.includes('all')
    const isAllChecked = types.includes('all')

    if (isAllChecked && !wasAllChecked) {
      // Just checked "all" - select all options and disable others
      form.account_types = [...accountTypeOptions.map((o) => o.value)]
    } else if (!isAllChecked && wasAllChecked) {
      // Just unchecked "all" - clear all
      form.account_types = []
    } else if (isAllChecked && types.length > 1) {
      // If "all" is checked but user tried to uncheck something else, keep only "all"
      form.account_types = ['all']
    }
  },
  { deep: true },
)

const isAllAccountTypesSelected = computed(() => form.account_types.includes('all'))

const hasAnyConditionData = computed(() => {
  return (
    form.exact_account.length > 0 ||
    form.keyword.trim().length > 0 ||
    !!form.gender ||
    !!form.age ||
    !!form.register_date_from ||
    !!form.register_date_to ||
    !!form.birthday_day ||
    !!form.birthday_month ||
    !!form.kyc_status ||
    form.account_types.length > 0 ||
    // NOTE: broker regions are only "committed" when RegionSelectComponent emits `selected` (user presses +)
    form.broker_area_regions.length > 0 ||
    !!form.device_type ||
    !!form.last_active
  )
})

const isSaveDisabled = computed(() => !hasAnyConditionData.value)

const validateForm = (): boolean => {
  const e: Record<string, string> = {}

  // Birthday validation
  const birthdayError = validateBirthday()
  if (birthdayError) e.birthday = birthdayError

  // Update errors object
  Object.keys(errors).forEach((key) => delete errors[key])
  Object.assign(errors, e)
  return Object.keys(e).length === 0
}

// ---- Region logic (RegionSelectComponent emits an array, same as BrokerProfile) ----
function handleSelectRegion(value: Region | Region[]) {
  const picked = Array.isArray(value) ? value : value != null ? [value] : []
  for (const region of picked) {
    if (region?.id == null) continue
    if (!form.broker_area_regions.find((r) => r.id === region.id)) {
      form.broker_area_regions.push(region)
    }
  }
}

function removeRegion(id: number | string) {
  form.broker_area_regions = form.broker_area_regions.filter((r) => r.id !== id)
}

// ---- Build label for condition tag ----
function buildLabel(): string {
  const parts: string[] = []

  if (form.exact_account.length) {
    const acc = form.exact_account[0]
    const name = acc?.name || acc?.phone || 'Tài khoản đã chọn'
    parts.push(`Chính xác TK: ${name}`)
  }
  if (form.keyword) parts.push(`Từ khóa: "${form.keyword}"`)
  if (form.gender) {
    const label = genderOptions.find((o) => o.value === form.gender)?.label
    parts.push(`Giới tính: ${label}`)
  }
  if (form.age) {
    const label = ageOptions.find((o) => o.value === form.age)?.label
    parts.push(`Tuổi: ${label}`)
  }
  if (form.register_date_from || form.register_date_to) {
    parts.push(`Ngày ĐK: ${form.register_date_from} → ${form.register_date_to}`)
  }
  if (form.birthday_day && form.birthday_month) {
    parts.push(`Ngày sinh: ${form.birthday_day}/${form.birthday_month}`)
  }
  if (form.kyc_status) {
    const label = kycOptions.find((o) => o.value === form.kyc_status)?.label
    parts.push(`KYC: ${label}`)
  }
  if (form.account_types.length) {
    const labels = form.account_types.map(
      (v) => accountTypeOptions.find((o) => o.value === v)?.label,
    )
    parts.push(`Tài khoản: ${labels.join(', ')}`)
  }
  if (form.broker_area_regions.length) {
    parts.push(`Khu vực: ${form.broker_area_regions.map((r) => r.name).join(', ')}`)
  }
  if (form.device_type) {
    const label = deviceOptions.find((o) => o.value === form.device_type)?.label
    parts.push(`Thiết bị: ${label}`)
  }
  if (form.last_active) {
    const label = lastActiveOptions.find((o) => o.value === form.last_active)?.label
    parts.push(`Hoạt động: ${label}`)
  }

  return parts.join(' | ') || 'Điều kiện mới'
}

function buildClauses(): ConditionClause[] {
  const clauses: ConditionClause[] = []

  // exact_account -> account_id in [ids]
  if (form.exact_account.length) {
    const ids = form.exact_account.map((a) => a?.id).filter((v) => v !== undefined && v !== null)
    if (ids.length) clauses.push({ key: 'account_id', op: 'in', value: ids })
  }

  // keyword -> name include
  if (form.keyword) {
    clauses.push({ key: 'name', op: 'include', value: form.keyword })
  }

  // gender -> eq
  if (form.gender) {
    clauses.push({ key: 'gender', op: 'eq', value: form.gender })
  }

  // age -> between (based on UI preset)
  if (form.age) {
    const map: Record<string, string> = {
      age_20_30: '20|30',
      age_30_45: '30|45',
      age_45_plus: '45|200',
    }
    const between = map[form.age]
    if (between) clauses.push({ key: 'age', op: 'between', value: between })
  }

  // regions -> in [ids]
  if (form.broker_area_regions.length) {
    console.log('[ConditionModal] broker_area_regions for clauses:', form.broker_area_regions)
    clauses.push({ key: 'regions', op: 'in', value: form.broker_area_regions.map((r) => r.id) })
  }

  // register date range
  if (form.register_date_from && form.register_date_to) {
    clauses.push({
      key: 'register_date',
      op: 'between',
      value: `${form.register_date_from}|${form.register_date_to}`,
    })
  } else if (form.register_date_from) {
    clauses.push({ key: 'register_date', op: 'gte', value: form.register_date_from })
  } else if (form.register_date_to) {
    clauses.push({ key: 'register_date', op: 'lte', value: form.register_date_to })
  }

  // birthday
  if (form.birthday_day) clauses.push({ key: 'birthday_day', op: 'eq', value: form.birthday_day })
  if (form.birthday_month)
    clauses.push({ key: 'birthday_month', op: 'eq', value: form.birthday_month })

  // kyc
  if (form.kyc_status) clauses.push({ key: 'kyc_status', op: 'eq', value: form.kyc_status })

  // account types
  if (form.account_types.length)
    clauses.push({ key: 'account_type', op: 'in', value: [...form.account_types] })

  // device
  if (form.device_type) clauses.push({ key: 'device_type', op: 'eq', value: form.device_type })

  // last active
  if (form.last_active) clauses.push({ key: 'last_active', op: 'eq', value: form.last_active })

  return clauses
}

const handleConfirm = () => {
  if (!validateForm()) {
    return
  }
  console.log('[ConditionModal] broker_area_regions at confirm:', form.broker_area_regions)
  const clauses = buildClauses()
  console.log('[ConditionModal] clauses at confirm:', clauses)
  const payload = { label: buildLabel(), clauses }
  console.log('[push-campaign][ConditionModal] on:confirm', payload)
  emit('on:confirm', payload)
  resetForm()
}

const handleClose = () => {
  resetForm()
  emit('on:close')
}

function resetForm() {
  form.exact_account = []
  form.keyword = ''
  form.gender = ''
  form.age = ''
  form.register_date_from = ''
  form.register_date_to = ''
  form.birthday_day = ''
  form.birthday_month = ''
  form.kyc_status = ''
  form.account_types = []
  form.broker_area_regions = []
  form.device_type = ''
  form.last_active = ''
  Object.keys(errors).forEach((key) => delete errors[key])
}

type ApiAccount = {
  id: number | string
  full_name?: string
  name?: string
  avatar?: unknown
  phone_number?: string
  phone?: string
  code?: string
  customer_profile?: unknown
  broker_profile?: unknown
}

async function fetchAccountsByIds(ids: Array<number | string>) {
  const uniqIds = [...new Set(ids)].filter((v) => v !== undefined && v !== null)
  if (!uniqIds.length) return []

  const results = await Promise.allSettled(
    uniqIds.map((id) => APIClient.get(AccountAPI.account.detail.replace(':id', String(id)))),
  )

  const fulfilled = results.filter((r) => r.status === 'fulfilled') as Array<
    PromiseFulfilledResult<unknown>
  >

  const extractPayload = (resp: unknown): unknown => {
    if (!resp || typeof resp !== 'object') return null
    const root = resp as Record<string, unknown>
    const data = root.data
    if (!data || typeof data !== 'object') return null
    const dataObj = data as Record<string, unknown>
    const inner = dataObj.data
    // support both {data:{data:...}} and {data:...}
    return inner ?? data
  }

  return fulfilled
    .map((r) => extractPayload(r.value))
    .filter(Boolean)
    .map((acc) => {
      const a = acc as ApiAccount
      return {
        id: a.id,
        name: a.full_name || a.name,
        phone: a.phone_number || a.phone,
        code: a.code,
        avatar: a.avatar,
        customer_profile: a.customer_profile,
        broker_profile: a.broker_profile,
      }
    })
}

async function hydrateFormFromClauses(clauses: ConditionClause[] | null | undefined) {
  resetForm()
  if (!clauses?.length) return

  for (const c of clauses) {
    if (!c?.key || !c?.op) continue

    if (c.key === 'account_id' && c.op === 'in') {
      const ids = Array.isArray(c.value) ? (c.value as Array<number | string>) : []
      const accounts = await fetchAccountsByIds(ids as Array<number | string>)
      form.exact_account = accounts
      continue
    }

    if (c.key === 'regions' && c.op === 'in') {
      const ids = Array.isArray(c.value) ? (c.value as Array<number | string>) : []
      const picked: Region[] = []
      const idSet = new Set(ids.map((v) => String(v)))

      type RegionConstLevel2 = { id: number | string; name: string }
      type RegionConstLevel1 = { id: number | string; name: string; children?: RegionConstLevel2[] }
      const regionTree = regionsConst as unknown as RegionConstLevel1[]

      for (const level1 of regionTree) {
        for (const level2 of level1.children || []) {
          if (idSet.has(String(level2.id))) {
            picked.push({
              id: level2.id,
              name: level2.name,
              level1: { name: level1.name },
            })
          }
        }
      }

      form.broker_area_regions = picked
      continue
    }

    if (c.key === 'name' && c.op === 'include') {
      form.keyword = String(c.value ?? '')
      continue
    }
    if (c.key === 'gender' && c.op === 'eq') {
      form.gender = String(c.value ?? '')
      continue
    }
    if (c.key === 'age' && c.op === 'between') {
      const raw = String(c.value ?? '')
      const [min, max] = raw.split('|')
      if (min === '20' && max === '30') form.age = 'age_20_30'
      else if (min === '30' && max === '45') form.age = 'age_30_45'
      else if (min === '45') form.age = 'age_45_plus'
      continue
    }
    if (c.key === 'register_date') {
      if (c.op === 'between') {
        const raw = String(c.value ?? '')
        const [from, to] = raw.split('|')
        form.register_date_from = from ?? ''
        form.register_date_to = to ?? ''
        continue
      }
      if (c.op === 'gte') {
        form.register_date_from = String(c.value ?? '')
        continue
      }
      if (c.op === 'lte') {
        form.register_date_to = String(c.value ?? '')
        continue
      }
    }
    if (c.key === 'birthday_day' && c.op === 'eq') {
      form.birthday_day = String(c.value ?? '')
      continue
    }
    if (c.key === 'birthday_month' && c.op === 'eq') {
      form.birthday_month = String(c.value ?? '')
      continue
    }
    if (c.key === 'kyc_status' && c.op === 'eq') {
      form.kyc_status = String(c.value ?? '')
      continue
    }
    if (c.key === 'account_type' && c.op === 'in') {
      form.account_types = Array.isArray(c.value) ? c.value.map((v) => String(v)) : []
      continue
    }
    if (c.key === 'device_type' && c.op === 'eq') {
      form.device_type = String(c.value ?? '')
      continue
    }
    if (c.key === 'last_active' && c.op === 'eq') {
      form.last_active = String(c.value ?? '')
      continue
    }
  }
}

watch(
  () => [props.show, props.initialClauses] as const,
  async ([show]) => {
    if (!show) return
    await hydrateFormFromClauses(props.initialClauses)
  },
)
</script>

<template>
  <Teleport to="body">
    <div
      v-if="props.show"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-[16px] shadow-[0px_4px_16px_rgba(0,0,0,0.18)] w-[1433px] h-[710px] flex flex-col overflow-clip"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-8 py-5 border-b border-[#e2e2e2] shrink-0">
          <span class="font-bold text-[22px] text-black">Tùy chỉnh đối tượng</span>
          <button
            class="flex items-center justify-center w-7 h-7 rounded hover:bg-[#f0f0f0] cursor-pointer"
            @click="handleClose"
          >
            <Icon icon="material-symbols-light:close" width="22" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto px-8 py-6 flex flex-col justify-between gap-[12px]">
          <!-- Chính xác tài khoản (left half only) -->
          <div class="flex items-center gap-[10px]">
            <div class="flex items-center justify-end shrink-0 w-[247px]">
              <label class="text-[14px] font-bold text-black whitespace-nowrap"
                >Chính xác tài khoản</label
              >
            </div>
            <div class="flex-1 max-w-[360px]">
              <AccountPicker
                :multiple="true"
                v-model="form.exact_account"
                placeholder="Chọn tài khoản"
              />
            </div>
          </div>

          <!-- Row: Từ khóa chứa | Giới tính -->
          <div class="flex gap-0">
            <!-- Left -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Từ khóa chứa</label
                >
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <input
                  v-model="form.keyword"
                  @input="form.keyword = form.keyword.replace(SPECIAL_CHAR_REGEX, '')"
                  :disabled="form.exact_account.length > 0"
                  :class="[
                    'bg-[#f7f7f7] border-none rounded-[4px] px-4 py-[16px] text-[13px] text-[#333] outline-none placeholder:text-[#777] w-full',
                    form.exact_account.length > 0 ? 'opacity-50 cursor-not-allowed' : '',
                  ]"
                  placeholder="Từ khóa trong tên, sdt, mã code"
                />
              </div>
            </div>
            <!-- Right -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap">Giới tính</label>
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.gender"
                  :options="genderOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn giới tính"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
            </div>
          </div>

          <!-- Row: Tuổi | Ngày đăng ký -->
          <div class="flex gap-0">
            <!-- Left -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap">Tuổi</label>
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.age"
                  :options="ageOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn độ tuổi"
                  appendTo="body"
                  class="w-full condition-select !p-0"
                />
              </div>
            </div>
            <!-- Right -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Ngày đăng ký</label
                >
              </div>
              <div class="flex flex-1 items-center gap-2 min-w-0">
                <input
                  v-model="form.register_date_from"
                  type="date"
                  :max="form.register_date_to || undefined"
                  class="bg-[#f7f7f7] border-none rounded-[4px] px-4 py-[13px] text-[13px] text-[#333] outline-none flex-1 min-w-0"
                />
                <span class="text-[#777] shrink-0">→</span>
                <input
                  v-model="form.register_date_to"
                  type="date"
                  :min="form.register_date_from || undefined"
                  class="bg-[#f7f7f7] border-none rounded-[4px] px-4 py-[13px] text-[13px] text-[#333] outline-none flex-1 min-w-0"
                />
              </div>
            </div>
          </div>

          <!-- Row: Ngày sinh | Tình trạng KYC -->
          <div class="flex gap-0">
            <!-- Left -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap">Ngày sinh</label>
              </div>
              <div class="flex items-center gap-[18px] shrink-0">
                <Select
                  v-model="form.birthday_day"
                  :options="filteredDayOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Ngày"
                  appendTo="body"
                  class="condition-select !p-0"
                />
                <span class="text-[16px] text-black shrink-0">/</span>
                <Select
                  v-model="form.birthday_month"
                  :options="monthOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Tháng"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
              <small v-if="errors.birthday" class="text-[#e03e3e] text-[12px]">{{
                errors.birthday
              }}</small>
            </div>
            <!-- Right -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Tình trạng KYC</label
                >
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.kyc_status"
                  :options="kycOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn tình trạng"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
            </div>
          </div>

          <!-- Tài khoản (full width) -->
          <div class="flex items-start gap-[10px]">
            <div class="flex items-center justify-end shrink-0 w-[247px] pt-[2px]">
              <label class="text-[14px] font-bold text-black whitespace-nowrap">Tài khoản</label>
            </div>
            <div
              class="flex flex-wrap gap-x-[40px] gap-y-[10px]"
              style="
                display: grid;
                grid-template-columns: repeat(3, fit-content(100%));
                gap: 10px 40px;
              "
            >
              <label
                v-for="opt in accountTypeOptions"
                :key="opt.value"
                class="flex gap-[7px] items-center cursor-pointer"
                :class="{
                  'cursor-not-allowed opacity-50': form.exact_account.length > 0,
                  'cursor-not-allowed': isAllAccountTypesSelected && opt.value !== 'all',
                }"
              >
                <input
                  type="checkbox"
                  :value="opt.value"
                  v-model="form.account_types"
                  :disabled="
                    form.exact_account.length > 0 ||
                    (isAllAccountTypesSelected && opt.value !== 'all')
                  "
                  class="accent-[#0035ba] w-[16px] h-[16px] shrink-0"
                />
                <span class="text-[12px] text-black whitespace-nowrap">{{ opt.label }}</span>
              </label>
            </div>
          </div>

          <!-- Khu vực môi giới (full width) -->
          <div class="flex items-start gap-[10px]">
            <div class="flex items-center justify-end shrink-0 w-[247px] pt-[2px]">
              <label class="text-[14px] font-bold text-black whitespace-nowrap"
                >Khu vực môi giới</label
              >
            </div>
            <div class="flex flex-col gap-2 flex-1 min-w-0 max-w-[360px]">
              <RegionSelectComponent :regions="[]" @selected="handleSelectRegion" />
              <hr v-if="form.broker_area_regions.length" class="border-[#e2e2e2]" />
              <div
                v-for="(region, idx) in form.broker_area_regions"
                :key="`region_${idx}`"
                class="flex items-center gap-1 text-[13px] text-[#333]"
              >
                {{ region.name }}, {{ region.level1?.name }}
                <span class="cursor-pointer text-[#e53935] ml-1" @click="removeRegion(region.id)">
                  <Icon icon="material-symbols:close" width="14" />
                </span>
              </div>
            </div>
          </div>

          <!-- Row: Loại thiết bị | Lần cuối hoạt động -->
          <div class="flex gap-0">
            <!-- Left -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Loại thiết bị sử dụng</label
                >
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.device_type"
                  :options="deviceOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn thiết bị"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
            </div>
            <!-- Right -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Lần cuối hoạt động</label
                >
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.last_active"
                  :options="lastActiveOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn thời gian"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-end gap-3 px-8 py-5 border-t border-[#e2e2e2] shrink-0"
        >
          <button
            :disabled="isSaveDisabled"
            :class="[
              'px-[22px] py-[14px] rounded-[4px] text-white text-[14px] font-bold shadow-[0px_4px_4px_rgba(0,0,0,0.25)]',
              isSaveDisabled
                ? 'bg-[#9b9b9b] cursor-not-allowed opacity-70'
                : 'bg-[#272727] hover:bg-[#444] cursor-pointer',
            ]"
            @click="handleConfirm"
          >
            Lưu đối tượng
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
:deep(.condition-select.p-select),
:deep(.condition-select .p-select-label) {
  background: #f7f7f7;
  border: none;
  font-size: 13px;
  padding: 13px 16px;
  color: #333;
}

:deep(.condition-select .p-select-label.p-placeholder) {
  color: #777;
}
</style>
